import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  useCreateStockTransferMutation,
  useGetLocationsQuery,
  useGetStockLevelsQuery
} from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';

function StockTransferForm() {
  const navigate = useNavigate();
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ productId: '', quantity: 1, productName: '' }]);

  const { data: locations } = useGetLocationsQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const [createTransfer, { isLoading: isSaving }] = useCreateStockTransferMutation();

  // Get stock levels for fromLocation to validate/show available qty
  const { data: stockLevelsData } = useGetStockLevelsQuery(
    { locationId: fromLocationId, limit: 1000 },
    { skip: !fromLocationId }
  );

  const products = productsData?.products || [];
  const stockLevels = stockLevelsData?.stockLevels || [];

  const handleAddItem = () => {
    setItems([...items, { productId: '', quantity: 1, productName: '' }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field === 'product') {
      newItems[index].productId = value?.id || '';
      newItems[index].productName = value?.name || '';
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromLocationId || !toLocationId || items.some(item => !item.productId || item.quantity <= 0)) {
      alert('Please fill all required fields correctly');
      return;
    }

    try {
      await createTransfer({
        fromLocationId,
        toLocationId,
        transferDate,
        notes,
        items: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
      }).unwrap();
      alert('Stock transfer completed successfully');
      navigate('/inventory/transfers');
    } catch (error) {
      alert(error?.data?.message || 'Error creating transfer');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/inventory/transfers')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          New Stock Transfer
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="From Location (Source)"
                value={fromLocationId}
                onChange={(e) => setFromLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="To Location (Destination)"
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                type="date"
                label="Transfer Date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Items to Transfer</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Available Stock</TableCell>
                  <TableCell width={150}>Quantity</TableCell>
                  <TableCell align="right" width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const stock = stockLevels.find(s => s.productId === item.productId);
                  const available = stock ? parseFloat(stock.quantity) - parseFloat(stock.reservedQuantity) : 0;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => option.name}
                          value={products.find(p => p.id === item.productId) || null}
                          onChange={(_, newValue) => handleItemChange(index, 'product', newValue)}
                          renderInput={(params) => <TextField {...params} label="Select Product" required size="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {products.find(p => p.id === item.productId)?.baseUnit || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color={available <= 0 ? 'error.main' : 'text.primary'}>
                          {available}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          required
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleRemoveItem(index)} disabled={items.length === 1}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            sx={{ mt: 2 }}
          >
            Add Item
          </Button>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/inventory/transfers')}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? 'Processing...' : 'Complete Transfer'}
        </Button>
      </Box>
    </Box>
  );
}

export default StockTransferForm;
